import {
  ContentCopy as ContentCopyIcon,
  Create as CreateIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as labels from "config.labels";
import React from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

// One selectable row of the Data Sources list. The Duplicate button is shown
// only when onDuplicate is supplied, so duplicate rows never offer it. A
// description, when given, is shown under the name.
const SourceRow = ({
  label,
  description,
  tooltip,
  selected,
  onToggle,
  onPreview,
  onDuplicate,
  onEdit,
  prefix = "",
  isLast = false,
}) => {
  const theme = useTheme();
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        cursor: "pointer",
        backgroundColor: selected ? "primary.50" : "transparent",
        "&:hover": {
          backgroundColor: selected ? "primary.100" : "action.hover",
        },
        borderBottom: "1px solid",
        borderColor: "divider",
        ...(isLast ? { "&:last-child": { borderBottom: "none" } } : {}),
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: 0.5,
          border: "2px solid",
          borderColor: selected ? "primary.main" : "grey.400",
          backgroundColor: selected ? "primary.main" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: theme.brand.white,
          fontSize: "0.75rem",
          fontWeight: 700,
        }}
      >
        {selected ? "✓" : null}
      </Box>
      <Tooltip
        title={tooltip ?? (description ? `${label}: ${description}` : label)}
        placement={"right"}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={"body2"}
            sx={{
              fontWeight: selected ? 500 : 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {`${prefix}${label}`}
          </Typography>
          {description && (
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              data-testid={"source-row-description"}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.3,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Tooltip>
      <Tooltip
        title={translateLabels(labels.REPORT_BUILDER_PREVIEW_TOOLTIP, {
          default: "Preview data",
        })}
      >
        <IconButton
          size={"small"}
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <VisibilityIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
      {typeof onEdit === "function" && (
        <Tooltip
          title={translateLabels(labels.REPORT_BUILDER_DUPLICATE_EDIT_TOOLTIP, {
            default: "Edit",
          })}
        >
          <IconButton
            size={"small"}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <CreateIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
      )}
      {typeof onDuplicate === "function" && (
        <Tooltip
          title={translateLabels(labels.REPORT_BUILDER_DUPLICATE_TOOLTIP, {
            default: "Duplicate",
          })}
        >
          <IconButton
            size={"small"}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <ContentCopyIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default SourceRow;
